import { appAuthRoutes } from '@/router/appAuthRoutes'
import { publicRoutes } from '@/router/publicRoutes'
import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import { useAccountStore } from '@/stores/accountStore'
import { useAppLockStore } from '@/stores/appLockStore'
import { useAccountReturnStore } from '@/stores/accountReturnStore'
import { useNavigationTransitionStore } from '@/stores/navigationTransitionStore'
import { useBrandStore } from '@/stores/brandStore'
import { useAppStore } from '@/stores/appStore'
import { isAccessDisabled } from '@/globals/typescript/utils/accessState'
import { createRouter, createWebHistory } from 'vue-router'
import type { TLabCustomerUserBusinessRole } from '@team-milva/lab-models/user'

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    requiresRole?: TLabCustomerUserBusinessRole
    // Full-screen guided flows (terms of service, app-lock) render without CustomerNav.
    hideShell?: boolean
    // Focused creation flows (e.g. adding a sample) slide the bottom nav out of view.
    hideBottomNav?: boolean
    // Overrides the transition-direction guard's path-depth inference for a
    // specific pair of routes — see its own comment below, and the one on
    // SelectOrderTat/PlaceOrder in appAuthRoutes.ts for why it's needed.
    flowOrder?: number
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Public
    ...publicRoutes,

    // Authenticated
    ...appAuthRoutes,

    // Catch all
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      meta: { requiresAuth: false },
      redirect: { name: 'Home' },
    },
  ],
})

// Auth guard
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  await new Promise<void>((resolve) => {
    const wait = setInterval(() => {
      if (!authStore.ready) return
      clearInterval(wait)
      resolve()
    }, 50)
  })

  const userStore = useUserStore()
  const accountStore = useAccountStore()
  const appLockStore = useAppLockStore()

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)

  if (requiresAuth && !authStore.user) {
    return next({ name: 'Home' })
  }

  // MIL-127 — app-lock.
  if (authStore.isLoggedIn) {
    if (appLockStore.isLocked && to.name !== 'AppLock') {
      return next({ name: 'AppLock' })
    }

    if (!appLockStore.isLocked && to.name === 'AppLock') {
      return next({ name: 'App' })
    }
  }

  if (authStore.isLoggedIn) {
    // One rule for every cause — the user, their lab account or their customer account being
    // disabled (MIL-419). The login form explains it via `reason`.
    if (
      isAccessDisabled({
        user: userStore.currentUser,
        labAccount: accountStore.labAccount,
        customerAccount: accountStore.customerAccount,
      })
    ) {
      await authStore.signOutWithoutRedirect()
      return next({ name: 'Home', query: { reason: 'deactivated' } })
    }

    if (userStore.needsTermsAcceptance && to.name !== 'TermsOfService') {
      return next({ name: 'TermsOfService' })
    }

    if (!userStore.needsTermsAcceptance && to.name === 'TermsOfService') {
      return next({ name: 'App' })
    }

    if (!requiresAuth) {
      return next({ name: 'App' })
    }

    const requiredRole = to.meta.requiresRole
    if (requiredRole && !userStore.isAdmin) {
      return next({ name: 'App' })
    }
  }

  next()
})

// Transition-direction guard — App.vue (page slide) and CustomerNav.vue
// (bottom-nav layout morph) read this to animate push/pop/crossfade. Depth is
// inferred from path segment count since every authenticated route is
// RESTfully nested (e.g. /app/projects/:id/orders/:orderId is naturally
// "deeper" than /app/projects) — no extra per-route meta needed for most
// routes. Sibling routes at the same depth (e.g. Projects ⇄ Orders) get a
// plain crossfade. The one documented exception is a route pair whose later
// step happens to sit at an equal-or-shallower path depth than the step
// before it (a wizard step nested under a sibling's path rather than being a
// true path child of it) — those opt into meta.flowOrder, compared here
// first whenever both sides of the navigation define it.
router.beforeEach((to, from, next) => {
  const transitionStore = useNavigationTransitionStore()
  const pathDepth = (path: string) => path.split('/').filter(Boolean).length

  if (!from.name) {
    transitionStore.setDirection('lateral')
  } else if (to.meta.flowOrder !== undefined && from.meta.flowOrder !== undefined) {
    if (to.meta.flowOrder > from.meta.flowOrder) transitionStore.setDirection('forward')
    else if (to.meta.flowOrder < from.meta.flowOrder) transitionStore.setDirection('back')
    else transitionStore.setDirection('lateral')
  } else {
    const toDepth = pathDepth(to.path)
    const fromDepth = pathDepth(from.path)
    if (toDepth > fromDepth) transitionStore.setDirection('forward')
    else if (toDepth < fromDepth) transitionStore.setDirection('back')
    else transitionStore.setDirection('lateral')
  }

  next()
})

// Title guard
router.beforeEach((to, _from, next) => {
  const name = to.name
  const routeTitle = typeof name === 'string' ? name : String(name ?? '')
  // The brand's name (EFI Global, Star Lab …), or 'Customer App' for the neutral build.
  const brandName = useBrandStore().getDisplayName(useAppStore().currentLocale)
  document.title = `${brandName} - ${routeTitle}`
  next()
})

// Last seen guard
router.beforeEach((to, _from, next) => {
  const authStore = useAuthStore()
  const userStore = useUserStore()

  if (authStore.isLoggedIn) {
    userStore.updateLastSeen()
  }

  next()
})

// Remembers where the user entered Account ("Mig") from, so its back button
// can return there — see accountReturnStore.
router.afterEach((to, from, failure) => {
  if (failure) return
  useAccountReturnStore().recordNavigation(to, from)
})

export default router
